import { ROUTES } from '@/lib/constants';
import type { MarketRowKey } from '@/lib/marketRows';
import type { SignupRole } from '@/lib/types';
import type { CidIconName } from '@/components/ui/cidIcons';
import type { FlowCategory } from './flowRoutes';

// Approved canideal.org homepage copy (homepage.html @ e2e6bcd). Do not reword.

export interface FlowProduct extends FlowCategory {
  label: string;
  icon: CidIconName;
}

export const FLOW_CATEGORIES: FlowProduct[] = [
  {
    name: 'flower',
    label: 'Flower & hemp',
    icon: 'leaf',
    color: '--cid-flow-flower',
    routeClass: 'green',
  },
  {
    name: 'extracts',
    label: 'Isolate & distillate',
    icon: 'flask-conical',
    color: '--cid-flow-extracts',
    routeClass: 'blue',
  },
  {
    name: 'oil',
    label: 'Oils & extracts',
    icon: 'droplet',
    color: '--cid-flow-oil',
    routeClass: 'cyan',
  },
  {
    name: 'supplies',
    label: 'Seeds & hardware',
    icon: 'package',
    color: '--cid-flow-supplies',
    routeClass: 'bottom',
  },
];

export const TRADE_SERVICES = [
  'Logistics',
  'Insurance',
  'Compliance',
  'Factoring',
  'Testing',
  'U.S. Banking',
];

export interface Executive {
  id: string;
  role: SignupRole;
  symbol: 'sales' | 'buying';
  icon: CidIconName;
  title: string;
  promise: string;
  /** Lines rendered with explicit breaks, as approved. */
  summary: string[];
  /** Workflow steps; each entry is two display lines. */
  steps: [string, string][];
  action: string;
}

/** Sales before buying. */
export const EXECUTIVES: Executive[] = [
  {
    id: 'cid-sales',
    role: 'vendor',
    symbol: 'sales',
    icon: 'leaf',
    title: 'AI Sales Executive',
    promise: 'An AI sales team for every product.',
    summary: ['Find qualified buyers. Market your products. Receive offers.'],
    steps: [
      ['Product', '& terms'],
      ['Buyer', 'fit'],
      ['Market &', 'outreach'],
      ['Receive', 'offers'],
      ['Review &', 'decide'],
    ],
    action: 'Sell products',
  },
  {
    id: 'cid-buying',
    role: 'retailer',
    symbol: 'buying',
    icon: 'package',
    title: 'AI Buying Executive',
    promise: 'An AI buying team for every requirement.',
    summary: ['Search global supply. Compare deals.', 'Find the right fit.'],
    steps: [
      ['Buying', 'criteria'],
      ['Global', 'search'],
      ['Compare', 'offers'],
      ['Review &', 'decide'],
    ],
    action: 'Source products',
  },
];

export interface MarketRow {
  key: MarketRowKey;
  country: string;
  measure: string;
  unit: string;
  icon: CidIconName;
  accent: string;
}

/**
 * Reported-market rows (presentation). Values come only from the governed
 * Terminal feed via src/lib/marketRows.ts (MARKET_ROW_SPECS, same keys); until
 * a row verifies it renders "Awaiting verified data" — never a hardcoded number.
 */
export const MARKET_ROWS: MarketRow[] = [
  {
    key: 'de',
    country: 'Germany',
    measure: 'Medical flower imports',
    unit: 'kg',
    icon: 'package-open',
    accent: 'var(--cid-market-de)',
  },
  {
    key: 'ca',
    country: 'Canada',
    measure: 'Medical dried cannabis sales',
    unit: 'packaged units',
    icon: 'bar-chart-3',
    accent: 'var(--cid-market-ca)',
  },
  {
    key: 'uy',
    country: 'Uruguay',
    measure: 'Pharmacy sales',
    unit: 'grams',
    icon: 'store',
    accent: 'var(--cid-market-uy)',
  },
];

export interface Tool {
  id?: string;
  tone?: 'crop' | 'routes';
  icon: CidIconName;
  title: string;
  body: string;
  href: string;
  action: string;
}

export const TOOLS: Tool[] = [
  {
    id: 'cid-iq',
    icon: 'messages-square',
    title: 'CanIDeal IQ',
    body: 'The conversational power people expect from ChatGPT, built specifically for cannabis commerce.',
    href: ROUTES.iq,
    action: 'Explore IQ →',
  },
  {
    id: 'cid-crop',
    tone: 'crop',
    icon: 'chart-no-axes-combined',
    title: 'Crop ROI Planner',
    body: 'Plan the crop. Test the economics.',
    href: ROUTES.cropRoi,
    action: 'Explore planner →',
  },
  {
    tone: 'routes',
    icon: 'network',
    title: 'Market Briefs & Podcast',
    body: 'Read the analysis. Hear the briefing.',
    href: ROUTES.marketBriefs,
    action: 'Explore briefs →',
  },
];
